import { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, Download, Loader2 } from "lucide-react";

// Configure worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface CertificatePDFViewerProps {
  pdfUrl: string;
  title: string;
  className?: string;
}

export default function CertificatePDFViewer({ pdfUrl, title, className = "" }: CertificatePDFViewerProps) {
  const [numPages, setNumPages] = useState<number>();
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  }

  function onDocumentLoadError(error: Error) {
    console.error('PDF loading error:', error);
    setError('PDF konnte nicht geladen werden');
    setLoading(false);
  }

  function goToPrevPage() {
    setPageNumber(page => Math.max(1, page - 1));
  }

  function goToNextPage() {
    setPageNumber(page => Math.min(numPages || 1, page + 1));
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-red-600">Fehler beim Laden des PDFs</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{error}</p>
          <Button 
            variant="outline" 
            className="mt-4"
            asChild
          >
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
              <Download className="h-4 w-4 mr-2" />
              PDF direkt herunterladen
            </a>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{title}</CardTitle>
          <Button 
            variant="outline" 
            size="sm"
            asChild
          >
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
              <Download className="h-4 w-4 mr-2" />
              Herunterladen
            </a>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="certificate-pdf-viewer">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2">PDF wird geladen...</span>
            </div>
          )}
          
          <div className="border rounded-lg overflow-hidden bg-gray-50">
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading=""
              className="flex justify-center"
            >
              <Page
                pageNumber={pageNumber}
                width={Math.min(600, window.innerWidth - 100)}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                className="shadow-sm"
              />
            </Document>
          </div>

          {/* Navigation Controls */}
          {numPages && numPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPrevPage}
                disabled={pageNumber <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Zurück
              </Button>
              
              <span className="text-sm text-muted-foreground">
                Seite {pageNumber} von {numPages}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}
              >
                Weiter
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Page indicator for single page */}
          {numPages === 1 && (
            <div className="flex justify-center mt-4">
              <span className="text-sm text-muted-foreground">
                1 Seite
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
